import { useEffect, useState } from "react";
import { Icon, MeCtx, go, isLead, isManager, useRoute } from "./shared.jsx";
import { createRoot } from "react-dom/client";
import "./styles.css";
import { api, auth } from "./api.js";
import { t } from "./i18n.js";
import { ToastHost } from "./ui.jsx";
import { Login, AcceptInvite } from "./pages/auth.jsx";
import { Dashboard } from "./pages/dashboard.jsx";
import { TaskList, TaskDetail } from "./pages/tasks.jsx";
import { ProjectList, ProjectDetail } from "./pages/projects.jsx";
import { TeamList, TeamDetail } from "./pages/teams.jsx";
import { Approvals } from "./pages/approvals.jsx";
import { Reports } from "./pages/reports.jsx";
import { People } from "./pages/people.jsx";
import { Settings } from "./pages/settings.jsx";
import { MyEvaluation } from "./pages/evaluation.jsx";
import { Notifications, More } from "./pages/more.jsx";

function navFor(me) {
  const base = [["/", "home", t("home")], ["/tasks", "tasks", t("myTasks")], ["/projects", "projects", t("projects")],
                ["/eval", "eval", t("myEval")]];
  const admin = [];
  if (isManager(me) || isLead(me)) admin.push(["/approvals", "approvals", t("approvals")]);
  if (isManager(me)) admin.push(["/teams", "teams", t("teams")], ["/reports", "reports", t("reports")]);
  else if (isLead(me)) admin.push(["/reports", "reports", t("reports")]);
  if (me.role === "admin") admin.push(["/people", "people", t("people")], ["/settings", "settings", t("settings")]);
  return { base, admin };
}

function Shell({ me, route, children }) {
  const { base, admin } = navFor(me);
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    let alive = true;
    const poll = () => api.get("/notifications?unread=1&page_size=1").then((r) => alive && setUnread(r.unread_count), () => {});
    poll();
    const id = setInterval(poll, 60_000);
    return () => { alive = false; clearInterval(id); };
  }, [route.path]);
  const active = (p) => (p === "/" ? route.path === "/" : route.path.startsWith(p));
  const link = ([p, icon, label]) => (
    <a key={p} href={"#" + p} className={"nav-item" + (active(p) ? " nav-on" : "")} aria-current={active(p) ? "page" : undefined}>
      <Icon name={icon} /><span>{label}</span>
    </a>
  );
  return (
    <div className="shell">
      <aside className="rail" aria-label="التنقل">
        <div className="brand"><span className="brand-mark" aria-hidden="true" />{t("appName")}<small>Smart Team</small></div>
        <nav>{base.map(link)}</nav>
        {admin.length > 0 && <nav className="rail-admin"><span className="rail-group">الإدارة</span>{admin.map(link)}</nav>}
        <div className="rail-foot">
          <a href="#/notifications" className={"nav-item" + (active("/notifications") ? " nav-on" : "")}>
            <Icon name="bell" /><span>{t("notifications")}</span>{unread > 0 && <span className="badge">{unread}</span>}
          </a>
          <a href="#/more" className="me-chip"><strong>{me.name}</strong><small>{t("role." + me.role)}</small></a>
        </div>
      </aside>
      <header className="topbar">
        <span className="brand"><span className="brand-mark" aria-hidden="true" />{t("appName")}</span>
        <a href="#/notifications" className="icon-btn" aria-label={t("notifications")}>
          <Icon name="bell" />{unread > 0 && <span className="badge">{unread}</span>}
        </a>
      </header>
      <main className="main" id="main">{children}</main>
      <nav className="tabbar" aria-label="التنقل الرئيسي">
        {[...base, ["/more", "more", t("more")]].map(link)}
      </nav>
    </div>
  );
}

function Routes({ route }) {
  const [a, b] = route.parts;
  switch (a) {
    case undefined: return <Dashboard />;
    case "tasks": return b ? <TaskDetail id={b} /> : <TaskList query={route.query} />;
    case "projects": return b ? <ProjectDetail id={b} /> : <ProjectList />;
    case "teams": return b ? <TeamDetail id={b} /> : <TeamList />;
    case "eval": return <MyEvaluation key={route.query.user || "me"} userId={route.query.user} month={route.query.month} />;
    case "approvals": return <Approvals />;
    case "reports": return <Reports />;
    case "people": return <People />;
    case "settings": return <Settings />;
    case "notifications": return <Notifications />;
    case "more": return <More />;
    default: return <div className="state"><strong>الصفحة غير موجودة</strong><a className="btn" href="#/">{t("home")}</a></div>;
  }
}

function App() {
  const route = useRoute();
  const [me, setMe] = useState(undefined);
  const [bootError, setBootError] = useState(null);
  const loadMe = () => {
    setBootError(null);
    if (!auth.hasSession()) { setMe(null); return; }
    api.get("/me").then(setMe, (e) => (e.status === 401 ? setMe(null) : setBootError(e)));
  };
  useEffect(() => { loadMe(); return auth.onChange(() => setMe(null)); }, []);

  if (route.parts[0] === "invite") return <AcceptInvite token={route.query.token} onDone={(u) => { setMe(u); go("/"); }} />;
  if (bootError) {
    return <div className="state state-error"><strong>تعذر الاتصال بالخادم</strong><span>{bootError.message}</span>
      <button className="btn" onClick={loadMe}>{t("retry")}</button></div>;
  }
  if (me === undefined) return <div className="state"><span className="spinner" />{t("loading")}</div>;
  if (me === null) return <Login onDone={(u) => setMe(u)} />;
  return (
    <MeCtx.Provider value={{ ...me, reload: loadMe }}>
      <Shell me={me} route={route}><Routes route={route} /></Shell>
    </MeCtx.Provider>
  );
}

createRoot(document.getElementById("root")).render(<ToastHost><App /></ToastHost>);
